import { forwardRef, type ElementType, type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Icon } from '../Icon';
import type { IconName, IconColor } from '../Icon';
import { LoadingIcon } from '../LoadingIcon';
import { Typography } from '../Typography';
import type { TypographyColor } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the ListItem component.
 *
 * `className` is intentionally omitted — ListItem is a sealed design system
 * component. Its layout, spacing, and typography are fixed by design.
 * To add a new visual state, add a named prop rather than reaching for a
 * className override.
 */
export interface ListItemProps extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'onClick'> {
  /** Primary label. Rendered as `header/h3`. */
  title: string;
  /**
   * Optional secondary line below the title.
   * Rendered as `body/small`; colour defaults to `bodylight` but can be
   * overridden via `subtitleColor` (e.g. `"error"` for a rejection reason).
   */
  subtitle?: string;
  /** Colour for the subtitle (e.g. `"error"` for a rejected task reason). @default "bodylight" */
  subtitleColor?: TypographyColor;
  /**
   * Optional icon to render on the left side of the row.
   * Rendered at size `large` (24px), inheriting `icon-default` colour.
   */
  icon?: IconName;
  /**
   * Optional icon to render on the right side of the row.
   * Common values: `"chevron-right"`, `"arrow-up-right-square"`.
   */
  trailingIcon?: IconName;
  /**
   * Semantic colour for the trailing icon (e.g. `"success"` for a completed check).
   * Defaults to the row's foreground colour.
   */
  trailingIconColor?: IconColor;
  /** Shows a spinner in the trailing slot (e.g. a task being verified) instead of an icon. */
  loading?: boolean;
  /** Greys the row and blocks interaction (e.g. a locked/not-yet-available item). */
  disabled?: boolean;
  /**
   * Click handler. When provided the root element becomes a `<button>`.
   * When omitted the root element is the value of `as` (default `"li"`).
   */
  onClick?: () => void;
  /**
   * The HTML element or React component to render as the root.
   * Ignored when `onClick` is provided (root is always `<button>` then).
   *
   * @default "li"
   */
  as?: ElementType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer ListItem component.
 *
 * A sealed row primitive used inside `List`. Renders a left icon, a title
 * (and optional subtitle), and a trailing icon. When `onClick` is provided
 * the row becomes an accessible `<button>`.
 *
 * ### Sealed component
 * `className` is not exposed. Layout (`p-4`, `gap-4`, `rounded-medium`) is
 * fixed by design. If a new visual state is needed, add a prop — do not
 * override via className.
 *
 * @example
 * <ListItem
 *   title="Data consent"
 *   icon="shield-check"
 *   trailingIcon="chevron-right"
 *   onClick={() => navigate('/data-consent')}
 * />
 *
 * @example
 * <ListItem
 *   title="Free products & deals"
 *   subtitle="Important for free products & deals"
 *   icon="plus-circle"
 *   trailingIcon="chevron-right"
 *   onClick={() => navigate('/deals')}
 * />
 */
export const ListItem = forwardRef<HTMLElement, ListItemProps>(function ListItem(
  {
    title,
    subtitle,
    subtitleColor = 'bodylight',
    icon,
    trailingIcon,
    trailingIconColor,
    loading,
    disabled,
    onClick,
    as: Component = 'li',
    ...props
  },
  ref
) {
  const isInteractive = typeof onClick === 'function';

  const Root = (isInteractive ? 'button' : Component) as ElementType;

  const rootProps = isInteractive ? { type: 'button' as const, onClick, disabled } : {};

  return (
    <Root
      ref={ref}
      className={cn(
        'flex items-center gap-4 p-4 w-full text-left',
        isInteractive && !disabled && 'cursor-pointer hover:bg-surface-secondary transition-colors',
        disabled && 'bg-disabled cursor-not-allowed'
      )}
      {...rootProps}
      {...props}
    >
      {/* Left icon */}
      {icon && (
        <span className={cn('flex-shrink-0', disabled ? 'text-disabled' : 'text-header')}>
          <Icon name={icon} size="large" aria-hidden="true" />
        </span>
      )}

      {/* Title + subtitle */}
      <span className="flex-1 flex flex-col gap-1 min-w-0">
        <Typography variant="header" size="h3" color={disabled ? 'disabled' : undefined}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body"
            size="small"
            color={disabled && subtitleColor === 'bodylight' ? 'bodylighter' : subtitleColor}
          >
            {subtitle}
          </Typography>
        )}
      </span>

      {/* Trailing: spinner while loading, otherwise the trailing icon */}
      {loading ? (
        <span className="flex-shrink-0">
          <LoadingIcon size="small" />
        </span>
      ) : (
        trailingIcon && (
          <span className="flex-shrink-0">
            <Icon
              name={trailingIcon}
              size="large"
              color={trailingIconColor ?? (disabled ? 'disabled' : undefined)}
              aria-hidden="true"
            />
          </span>
        )
      )}
    </Root>
  );
});

export default ListItem;
